/**
 * TaskKanban — Kanban board with 3 columns and native HTML5 drag-and-drop
 */

import { useState } from "react";
import type { AMTask, TaskStatus } from "@/hooks/useAMTasks";
import TaskCard from "@/components/am/TaskCard";

const columns: { status: TaskStatus; label: string; color: string }[] = [
  { status: "todo", label: "To Do", color: "border-t-slate-400" },
  { status: "in_progress", label: "In Progress", color: "border-t-blue-500" },
  { status: "done", label: "Done", color: "border-t-green-500" },
];

interface TaskKanbanProps {
  tasks: AMTask[];
  agentNames: Map<string, string>;
  onTaskClick: (task: AMTask) => void;
  onMoveTask: (taskId: string, newStatus: TaskStatus) => void;
}

export default function TaskKanban({ tasks, agentNames, onTaskClick, onMoveTask }: TaskKanbanProps) {
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData("text/plain", taskId);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(taskId);
  };

  const handleDragOver = (e: React.DragEvent, columnStatus: TaskStatus) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnStatus);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, columnStatus: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("text/plain");
    if (taskId) {
      onMoveTask(taskId, columnStatus);
    }
    setDragOverColumn(null);
    setDraggingId(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {columns.map((col) => {
        const columnTasks = tasks
          .filter((t) => t.status === col.status)
          .sort((a, b) => {
            // Sort by priority (urgent first), then due date
            const prioOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
            const prioDiff = prioOrder[a.priority] - prioOrder[b.priority];
            if (prioDiff !== 0) return prioDiff;
            if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
            if (a.dueDate) return -1;
            if (b.dueDate) return 1;
            return 0;
          });

        const isOver = dragOverColumn === col.status;

        return (
          <div
            key={col.status}
            className={`rounded-lg border-t-4 ${col.color} bg-muted/30 p-3 min-h-[200px] transition-colors ${
              isOver ? "bg-primary/5 ring-2 ring-primary/20" : ""
            }`}
            onDragOver={(e) => handleDragOver(e, col.status)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, col.status)}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">{col.label}</h3>
              <span className="text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                {columnTasks.length}
              </span>
            </div>

            <div className="space-y-2">
              {columnTasks.map((task) => (
                <div
                  key={task.id}
                  className={`transition-opacity ${draggingId === task.id ? "opacity-50" : ""}`}
                >
                  <TaskCard
                    task={task}
                    agentName={task.agentId ? agentNames.get(task.agentId) : undefined}
                    onClick={() => onTaskClick(task)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id)}
                  />
                </div>
              ))}
              {columnTasks.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">
                  No tasks
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
